import React from "react";
import { Icono, IconBubble } from "../iconos";
import "./zm-ui.css";

const STATUS_STYLES = {
    nuevo: "is-new",
    contactado: "is-contact",
    calificado: "is-qualified",
    demo: "is-demo",
    cerrado: "is-closed",
    rechazado: "is-rejected",
    sin_interes: "is-rejected",
    active: "is-active",
    paused: "is-paused",
    archived: "is-muted",
    deleted: "is-muted",
    alta: "is-alta",
    media: "is-media",
    baja: "is-baja",
};

const STATUS_LABELS = {
    nuevo: "Nuevo",
    contactado: "Contactado",
    calificado: "Calificado",
    demo: "Demo",
    cerrado: "Cerrado",
    rechazado: "Rechazado",
    sin_interes: "Sin interes",
    active: "Activa",
    paused: "Pausada",
    archived: "Archivada",
    deleted: "Eliminada",
    alta: "Alta",
    media: "Media",
    baja: "Baja",
};

const normalize = (value) => String(value || "").toLowerCase().trim().replace(/\s+/g, "_");

const axisBase = {
    border: { display: false },
    grid: { color: "rgba(148, 163, 184, 0.16)", drawTicks: false },
    ticks: {
        color: "#5f6f86",
        padding: 8,
        font: { size: 11, weight: "600" },
    },
    title: {
        display: false,
        color: "#5f6f86",
        font: { size: 11, weight: "700" },
    },
};

export function buildZmChartOptions({
    leftTitle,
    rightTitle,
    showRightAxis = false,
    hideLegend = false,
    legendPosition = "top",
    darkMode = false,
} = {}) {
    const labelColor = darkMode ? "rgba(226, 232, 240, 0.84)" : "#334155";
    const axisColor = darkMode ? "rgba(226, 232, 240, 0.72)" : "#5f6f86";
    const gridColor = darkMode ? "rgba(148, 163, 184, 0.10)" : "rgba(148, 163, 184, 0.16)";
    const rightGridColor = darkMode ? "rgba(148, 163, 184, 0.08)" : "rgba(148, 163, 184, 0.14)";

    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        animation: { duration: 450 },
        layout: { padding: { top: 4, right: 8, left: 4, bottom: 0 } },
        plugins: {
            legend: hideLegend ? {
                display: false,
            } : {
                position: legendPosition,
                align: "center",
                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 14,
                    color: labelColor,
                    font: { size: 12, weight: "600" },
                },
            },
            tooltip: {
                backgroundColor: "rgba(17, 24, 39, 0.94)",
                titleColor: "#f8fafc",
                bodyColor: "#e2e8f0",
                borderColor: "rgba(255,255,255,0.08)",
                borderWidth: 1,
                padding: 10,
                displayColors: true,
                usePointStyle: true,
            },
        },
        scales: {
            x: {
                ...axisBase,
                grid: { display: false },
                ticks: {
                    ...axisBase.ticks,
                    color: axisColor,
                    maxRotation: 0,
                    autoSkipPadding: 12,
                },
            },
            y: {
                ...axisBase,
                beginAtZero: true,
                grid: { color: gridColor, drawTicks: false },
                ticks: {
                    ...axisBase.ticks,
                    color: axisColor,
                },
                title: {
                    ...axisBase.title,
                    display: Boolean(leftTitle),
                    color: axisColor,
                    text: leftTitle,
                },
            },
            ...(showRightAxis
                ? {
                    y1: {
                        ...axisBase,
                        beginAtZero: true,
                        position: "right",
                        grid: { drawOnChartArea: false, color: rightGridColor },
                        ticks: {
                            ...axisBase.ticks,
                            color: axisColor,
                        },
                        title: {
                            ...axisBase.title,
                            display: Boolean(rightTitle),
                            color: axisColor,
                            text: rightTitle,
                        },
                    },
                }
                : {}),
        },
    };
}

export function ZMLayout({ title, subtitle, right, children, className = "" }) {
    return (
        <div className={`zm-layout ${className}`}>
            <div className="zm-page-header">
                <div>
                    <h2>{title}</h2>
                    {subtitle && <p>{subtitle}</p>}
                </div>
                {right && <div className="zm-page-header-right">{right}</div>}
            </div>
            {children}
        </div>
    );
}

export function ZMCard({ title, subtitle, actions, children, className = "", bodyClassName = "" }) {
    return (
        <section className={`zm-card ${className}`}>
            {(title || subtitle || actions) && (
                <header className="zm-card-header">
                    <div>
                        {title && <h3>{title}</h3>}
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                    {actions && <div>{actions}</div>}
                </header>
            )}
            <div className={`zm-card-body ${bodyClassName}`}>{children}</div>
        </section>
    );
}

export function ZMMetricCard({ icon = <Icono.Users />, label, value, helper, empty = false }) {
    return (
        <ZMCard className="zm-metric-card" bodyClassName="zm-metric-content">
            <div className="zm-metric-top">
                <IconBubble color="#8dc63f" size={32}>{icon}</IconBubble>
                <span>{label}</span>
            </div>
            <div className={`zm-metric-value ${empty ? "is-empty" : ""}`}>{empty ? "sin datos" : value}</div>
            {helper && <div className="zm-metric-helper">{helper}</div>}
        </ZMCard>
    );
}

export function ZMTable({ columns = [], rows = [], emptyText = "Sin datos", className = "" }) {
    return (
        <div className={`zm-table-wrap ${className}`}>
            <table className="zm-table">
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key || col.header}
                                className={col.className || (col.cellClassName && (col.cellClassName.includes("text-end") || col.cellClassName.includes("text-center")) ? col.cellClassName : "") || ""}
                            >
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td className="zm-table-empty" colSpan={Math.max(columns.length, 1)}>
                                {emptyText}
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, index) => (
                            <tr key={row.id || row.key || index}>
                                {columns.map((col) => (
                                    <td key={col.key || col.header} className={col.cellClassName || ""}>
                                        {typeof col.render === "function" ? col.render(row, index) : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

export function ZMChartBox({ children, className = "" }) {
    return <div className={`zm-chart-wrap ${className}`}>{children}</div>;
}

export function ZMBadge({ status, label }) {
    const key = normalize(status);
    const tone = STATUS_STYLES[key] || "is-muted";
    const text = label || STATUS_LABELS[key] || status || "-";
    return <span className={`zm-badge ${tone}`}>{text}</span>;
}

export function ZMFiltersBar({ children, className = "" }) {
    return <div className={`zm-filters-bar ${className}`}>{children}</div>;
}

export function ZMEmptyState({ title = "Sin datos disponibles", hint = "Revisar filtros o permisos de Meta." }) {
    return (
        <ZMCard className="zm-empty-card">
            <div className="zm-empty-state">
                <div className="zm-empty-icon">
                    <Icono.AlertTriangle />
                </div>
                <h4>{title}</h4>
                <p>{hint}</p>
            </div>
        </ZMCard>
    );
}

export function ZMLoadingState({ label = "Cargando datos...", fullScreen = false }) {
    return (
        <div className={`zm-state-wrap ${fullScreen ? "is-fullscreen" : ""}`}>
            <div className="zm-state-panel">
                <div className="zm-spinner" />
                <span>{label}</span>
            </div>
        </div>
    );
}

export function ZMErrorState({ message = "No se pudo cargar la informacion.", onRetry }) {
    return (
        <ZMCard className="zm-error-card">
            <div className="zm-error-state">
                <strong>{message}</strong>
                {typeof onRetry === "function" && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
                        Reintentar
                    </button>
                )}
            </div>
        </ZMCard>
    );
}
